import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchBar from '../../components/SearchBar/SearchBar';
import Pagination from '../../components/Pagination/Pagination';
import HealthCheckCard from '../../components/HealthCheckCard/HealthCheckCard';
import Cookies from 'js-cookie';
import DropdownCheckboxList from '../../components/Checkbox/Checkbox';
import countries from 'i18n-iso-countries';
import { ImSortAlphaDesc, ImSortAlphaAsc } from "react-icons/im";
countries.registerLocale(require('i18n-iso-countries/langs/en.json'));

const getCountryName = (isoCode) => {
    if (isoCode && countries.isValid(isoCode)) {
        return countries.getName(isoCode, 'en'); // 'en' specifies the language (English)
    }
    return 'Unknown';
};

function HealthCheck() {
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [domainsPerPage] = useState(21);
    const [healthCheck, setHealthCheck] = useState(null);
    const [pools, setPools] = useState([]);
    const [selectedPool, setSelectedPool] = useState('');
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [countriesList, setCountries] = useState([]);
    const [sortOrder, setSortOrder] = useState('asc'); // New state for sorting order
    const navigate = useNavigate(); 

    useEffect(() => {
        const fetchHealthCheck = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/health-check`, {
                    credentials: "include"
                });
                if (response.status === 401) {
                    Cookies.remove('session-id');
                    navigate('/login');
                }
                const body = await response.json();
                setHealthCheck(body);
                const uniqueCountryNames = Array.from(new Set(
                    Object.keys(body).flatMap(key => 
                        Object.keys(body[key].countries).map(getCountryName)
                    )
                ));
                setCountries(uniqueCountryNames);
                const storedSelectedCountries = JSON.parse(localStorage.getItem('selectedCountries'));
                if (storedSelectedCountries) {
                    setSelectedCountries(storedSelectedCountries);
                } else {
                    setSelectedCountries(uniqueCountryNames);
                }
            } catch (error) {
                console.error('Error fetching health check data:', error);
            }
        };
        fetchHealthCheck();
    }, []);

    useEffect(() => {
        const fetchPools = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/pool`, {
                    credentials: "include"
                });
                if (response.status === 401) {
                    Cookies.remove('session-id');
                    navigate('/login');
                }
                const body = await response.json();
                setPools(body.data);
            } catch (error) {
                console.error('Error fetching pools data:', error);
            }
        };
        fetchPools();
    }, []);

    useEffect(() => {
        const storedSelectedPool = localStorage.getItem('selectedPool');
        if (storedSelectedPool) {
            setSelectedPool(storedSelectedPool);
        }
    }, []);

    const refreshHealthCheck = async (hostname) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/health-check?hostname=${hostname}`, {
                credentials: "include",
                method: "PUT"
            });
            if (response.status === 401) {
                Cookies.remove('session-id');
                navigate('/login');
            }
            if (response.ok) {
                setHealthCheck(prevHealthCheck => {
                    const updatedHealthCheck = { ...prevHealthCheck };
                    delete updatedHealthCheck[hostname];
                    return updatedHealthCheck;
                });
            }
        } catch (error) {
            console.error('Error fetching health check data:', error);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleSearchChange = (query) => {
        setSearchQuery(query);
        setCurrentPage(1);
    };

    const handlePoolFilterChange = (event) => {
        const selectedValue = event.target.value;
        setSelectedPool(selectedValue);
        localStorage.setItem('selectedPool', selectedValue);
        setCurrentPage(1); 
    };

    const handleCountrySelect = (selectedItems) => {
        setSelectedCountries(selectedItems);
        localStorage.setItem('selectedCountries', JSON.stringify(selectedItems));
    };

    const handleSortChange = () => {
        setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
    };

    if (healthCheck === null) {
        return <div>Loading...</div>;
    }

    let filteredHealthCheck = Object.entries(healthCheck).filter(([domain, details]) => {
        const matchesSearch = domain.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesPool = selectedPool ? details.pools.includes(selectedPool) : true;
        const matchesCountry = selectedCountries.length > 0
            ? Object.keys(details.countries).some(countryCode => selectedCountries.includes(getCountryName(countryCode)))
            : true;
        return matchesSearch && matchesPool && matchesCountry;
    });

    // Sort the filtered health check data based on the sort order
    filteredHealthCheck = filteredHealthCheck.sort(([domainA], [domainB]) => {
        if (sortOrder === 'asc') {
            return domainA.localeCompare(domainB);
        } else {
            return domainB.localeCompare(domainA);
        }
    });

    const totalPages = Math.ceil(filteredHealthCheck.length / domainsPerPage);
    const startIndex = (currentPage - 1) * domainsPerPage;
    const currentHealthCheck = filteredHealthCheck.slice(startIndex, startIndex + domainsPerPage);

    const truncateString = (str, length) => {
        return str.length > length ? str.slice(0, length) + '...' : str;
    };

    return (
        <div className='p-4 max-w-5xl mx-auto'>
            <div className="w-full flex flex-row gap-4 mt-10 p-2 items-center">
                <div className='min-w-96'>
                    <SearchBar placeholder="Search by hostname.." onSearch={handleSearchChange} />
                </div>
                <select
                    value={selectedPool}
                    onChange={handlePoolFilterChange}
                    className="p-2 border rounded-lg max-w-fit text-sm font-semibold text-gray-700 border-gray-300"
                >
                    <option value="">All</option>
                    {pools.map((pool) => (
                        <option key={pool.id} value={pool.title}>
                            {truncateString(pool.title, 20)}
                        </option>
                    ))}
                </select>

                <DropdownCheckboxList
                    items={countriesList}
                    selectedItems={selectedCountries}
                    onChange={handleCountrySelect}
                />

                <button 
                    onClick={handleSortChange} 
                    className="border flex flex-row px-3 py-2 rounded-lg gap-2 font-semibold text-gray-800 border-gray-300 hover:text-blue-500"
                >
                    {sortOrder === 'asc' ? <ImSortAlphaAsc className="h-5 w-5" /> : <ImSortAlphaDesc className="h-5 w-5" />}
                </button>
            </div>

            {filteredHealthCheck.length === 0 ? (
                <div className="my-10 text-xl font-semibold text-gray-500 text-center min-h-40">No health check info found.</div>
            ) : (
                <div className="flex flex-wrap justify-center">
                    {currentHealthCheck.map(([domain, details]) => {
                        const filteredCountries = Object.entries(details.countries)
                            .filter(([countryCode]) => selectedCountries.includes(getCountryName(countryCode)))
                            .reduce((acc, [countryCode, countryDetails]) => {
                                acc[countryCode] = countryDetails;
                                return acc;
                            }, {});
                        return (
                            <HealthCheckCard key={domain} domain={domain} pools={details.pools} regions={filteredCountries} refreshHealthCheck={refreshHealthCheck} />
                        );
                    })}
                </div>
            )}

            {filteredHealthCheck.length > 0 && (
                <Pagination 
                    currentPage={currentPage} 
                    totalPages={totalPages} 
                    onPageChange={handlePageChange} 
                />
            )}
        </div>
    );
}

export default HealthCheck;
